import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "Download as Markdown" button.
 *
 * Each caller supplies (a) a filename prefix and (b) a `buildMarkdown` callback
 * that returns the full .md string. The button handles all the browser plumbing
 * (Blob, URL.createObjectURL, hidden <a>.click(), cleanup) plus a success toast.
 *
 * Usage:
 *   <DownloadMdButton
 *     filenamePrefix="inphase-keywords"
 *     buildMarkdown={() => `# Keywords\n\n${rows.map(...).join("\n")}`}
 *   />
 */
export function DownloadMdButton({
    filenamePrefix,
    buildMarkdown,
    disabled = false,
    testId = "download-md-btn",
    label = "Export .md",
    className = "",
}) {
    const [busy, setBusy] = useState(false);

    const onClick = async () => {
        setBusy(true);
        try {
            const md = await Promise.resolve(buildMarkdown());
            if (!md || !md.trim()) {
                toast.warning("Nothing to export yet — load some data first.");
                return;
            }
            const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
            const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${filenamePrefix}-${stamp}.md`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Downloaded ${a.download}`);
        } catch (err) {
            toast.error(err?.message || "Couldn't build markdown export");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy || disabled}
            data-testid={testId}
            title="Download this section as a Markdown file"
            className={`border border-border px-3 py-2 text-xs font-mono uppercase tracking-widest hover:border-primary hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2 ${className}`}
        >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            {label}
        </button>
    );
}
